import { xlationContext, xlate } from "../../../../framework/utils/i18n";

xlationContext('Markdown help articles');

export interface IHelpArticle {
  articleId: string;
  title: string;
  content: string[];
}

// TO END A TIMED SECTION SAMPLE, PUT THE FOLLOWING STRING AT THE BOTTOM: #end$

export const MarkdownHelpArticles: IHelpArticle[] = [
  // overview
  {
    articleId: 'overview',
    title: xlate('Overview'),
    content: [
      xlate('# Discussion Planning Overview'),
      '',
      xlate("One of Presentual's key features is the ability to plan both the pace and direction of a discussion in just one place."),
      xlate('This is made possible by *Presentual Markdown*, a "flavor" (variation) of the widely-known [Markdown](https://www.markdownguide.org/getting-started/) rich document authoring language.'),
      xlate('With standard Markdown, an author can create a document with bold and/or italicized text, headings and subheadings, lists, even images and tables - all with just plain text.'),
      xlate('This makes Markdown the perfect language for discussion planning -- it is very easy to prepare well-formatted notes quickly.'),
      xlate('Presentual Markdown implements most features of standard Markdown, and expands upon them with special syntax for organizing notes into dynamically timed sections.'),
      xlate('To learn about any of these features and use them in your discussion plan, click/tap on the corresponding tab above.'),
      '',
      xlate('## Presentual Markdown Features'),
      '',
      xlate('### Document Elements'),
      '',
      xlate('- Timed sections'),
      xlate('- Headings and subheadings'),
      xlate('- Website links (hyperlinks)'),
      xlate('- Images'),
      xlate('- Tables'),
      xlate('- Horizontal separator lines'),
      xlate('- Blockquotes'),
      xlate('- Dash (ndash) and long dash (mdash) characters'),
      '',
      xlate('### Text Decoration'),
      '',
      xlate('- **Bold** text'),
      xlate('- *Italic* text'),
      xlate('- ***Bold + Italic*** text'),
      xlate('- ===Underlined=== text'),
      xlate('- ==Highlighted== text'),
      xlate('- ~~Strikethrough~~ text'),
      xlate('- Subscript~(subscript)~ text'),
      xlate('- Superscript^(superscript)^ text'),
      xlate('- `Code` text'),
      '',
      xlate('## Explore Presentual Markdown'),
      '',
      xlate('To learn more about any of the features of Presentual Markdown and use them in your notes, click or tap on the corresponding tab above.'),
    ]
  },
  // timed section
  {
    articleId: 'timed-section',
    title: xlate('Timed Section'),
    content: [
      xlate('# Timed Section'),
      '',
      xlate('A key feature of Presentual is the ability to subdivide a discussion plan into individually timed sections.'),
      xlate('This approach provides far greater control over the pace of the discussion, as adhering to the time allotted to one section of the discussion ensures that there will be sufficient time to cover the remaining points of discussion.'),
      xlate('If, during the course of the discussion, it becomes desirable to spend more time than planned on a particular section, this may be done with an accurate understanding of how the timing of the remaining sections will be impacted.'),
      '',
      xlate('> Each section of a discussion plan should be a natural boundary in the overall discussion,'),
      xlate('e.g. each key point of exposition in a lecture, each paragraph in a review of printed material, each major item on a meeting agenda, each main question for an interview, etc.'),
      '',
      xlate('The time allotted to each section, and optionally, its title, is denoted with a *section heading*.'),
      xlate('A section heading follows this format:'),
      '',
      xlate('> `[Timing Method][Timing Value] [Title]`'),
      '',
      xlate('### Section Heading Components'),
      '',
      xlate(' - **Timing Method** -- Designates how Presentual is allowed to make adjustments to the time allocated to the section during the discussion.  Possible values are `$` (flexible) or `#` (fixed)'),
      xlate(' - **Timing Value** -- A representation of the time allotted to the section, expressed as a single number, or as `0:00 ([minutes]:[seconds])`'),
      xlate(' - **Title** -- Title text for the section.  This is optional.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `$3:00 Introduction`'),
      '>',
      xlate('> ## Important'),
      '>',
      xlate('> `Notice that the content of a section is made up of everything under its section heading... `'),
      '>',
      xlate('> `$5:00 Another Section`'),
      '>',
      xlate('> `...until either the another section heading is encountered, or the end of the discussion plan is reached`'),
      '',
      xlate('#### Result'),
      '',
      xlate('$3:00 Introduction'),
      '',
      xlate('## Important'),
      '',
      xlate('Notice that the content of a section is made up of everything under its section heading... '),
      '',
      xlate('#end$'),
      '',
      xlate('$5:00 Another Section'),
      '',
      xlate('...until either another section heading is encountered, or the end of the discussion plan is reached'),
      '',
      xlate('#end$'),
      '',
      xlate('## Section Timing Methods'),
      '',
      xlate('There are three ways to indicate how Presentual should determine the amount of time allotted to a section during a discussion:'),
      '',
      xlate(' - Flexible Duration'),
      xlate(' - Flexible Share'),
      xlate(' - Fixed Duration'),
      '',
      xlate('### Flexible Duration Timing'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `$5:00 Flexible Duration Timing`'),
      '',
      xlate('#### Result'),
      '',
      xlate('$5:00 Flexible Duration Timing'),
      '',
      xlate('#end$'),
      '',
      xlate('When using flexible duration timing, the *ideal* amount of time (in [minutes]:[seconds]) allotted to the section is provided.'),
      xlate('The "ideal" amount of time refers to the time allotted to the section if its discussion began precisely on time.'),
      xlate('In practice, this is not likely to happen, so Presentual will proportionally adjust the amount of time allotted to the section according to the actual time available at the point in the live discussion when the section is reached.'),
      '',
      xlate('> Presentual scales flexible time allotments **in both directions**.'),
      xlate('If discussion of a section begins ahead of schedule, Presentual will scale the time allotted to the section up, granting it more time than originally planned.'),
      xlate('If on the other hand, discussion of a section begins behind schedule, Presentual will scale the time allotted to the section down so as to end the discussion on time.'),
      '',
      xlate('### Flexible Share Timing'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `$2 Flexible Share Timing`'),
      '',
      xlate('#### Result'),
      '',
      xlate('$2 Flexible Share Timing'),
      '',
      xlate('#end$'),
      '',
      xlate('When implementing flexible share timing, a single number is provided.'),
      xlate('This value refers to the number of shares of time available for the discussion afforded to a section.'),
      '',
      xlate('Consider this discussion plan, made up of three sections:'),
      '',
      xlate('> `$1 Section A`'),
      xlate('> `$2 Section B`'),
      xlate('> `$3 Section C`'),
      '',
      xlate('The total number of shares in the discussion plan is 6 (1 share for Section A + 2 shares for Section B + 3 shares for Section C = 6 shares).'),
      xlate('Suppose that this is a 30-minute discussion.  The time available for the discussion will be divided by the total number of shares, so each share will be equal to 5 minutes.'),
      xlate('Section A will therefore be allotted 5 minutes (1 5-minute share), Section B 10 minutes (2 5-minute shares), and Section C 15 minutes (3 5-minute shares).'),
      '',
      xlate('Flexible share timing gives discussion planners a "quick-and-dirty" way to allocate time to each section, by simply expressing how much more or less of the overall discussion time should be allocated to one section in comparison to another. '),
      '',
      xlate('> Whenever only flexible and fixed timing is used in a discussion plan, Presentual will use the durations specified on each section to infer the intended duration of the overall discussion.'),
      xlate('However, if one or more sections in a discussion plan uses flexible share timing, Presentual is unable to make this inference.'),
      xlate('The discussion duration must be provided, either along with the discussion plan (recommended), or just before the discussion begins.'),
      '',
      xlate('#### Flexible Share Timing Compatibility'),
      '',
      xlate('A discussion plan may contain sections with flexible share timing alongside sections with flexible duration timing, sections with fixed duration timing (discussed below), or a mix of the three.'),
      xlate('When this is the case, Presentual will first reserve the time to be allotted to sections using flexible and fixed duration timing, then allocate the time remaining to the sections with flexible share timing as described above.'),
      '',
      xlate('### Fixed Duration Timing'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `#5:00 Fixed Duration Timing`'),
      '',
      xlate('#### Result'),
      '',
      xlate('#5:00 Fixed Duration Timing'),
      '',
      xlate('#end$'),
      '',
      xlate('When the time allotted to a section is defined using fixed duration timing, the *actual* amount of time (in [minutes]:[seconds]) allotted to the section is provided.'),
      xlate('Unlike sections using the other two timing methods, the time allotted to a section using fixed duration timing remains locked at the specified duration.'),
      xlate('It does not adjust based on whether the overall discussion is behind or ahead of schedule when discussion of the fixed duration section begins.'),
      '',
      xlate('This timing method is useful when, for example, a portion of the discussion includes playing a video.'),
      xlate('It may not be possible to shorten the video, in which case it would not make sense to allow the timing of the section when the video is played to scale.'),
      '',
      xlate('> **Use fixed timing sparingly!**  A key advantage of leading a discussion with Presentual is its flexiblility with timing.'),
      xlate('This advantage is lost when fixed duration timing allotments are used too often.'),
    ]
  },
  // escaping
  {
    articleId: 'escaping',
    title: xlate('Escaping'),
    content: [
      xlate('# Escaping Markdown'),
      '',
      xlate('Certain text characters have special meaning in Presentual Markdown. '),
      xlate('==Using these characters in a certain sequence may inadvertently lead to unexpected results:'),
      '',
      xlate('### Example'),
      '',
      xlate('> `Over 5,000 years of art from around the world is on display at the Metropolitan Museum of Art*. '),
      xlate('==The author does not intend to display this text in italics**;'),
      xlate('==the asterisks in the text are used to indicate the presence of footnotes. '),
      xlate('==However, Presentual Markdown interprets them as markers for the beginning and end of a span of emphasized text.`'),
      '>',
      xlate('> `*Located in New York City, New York, United States of America.`'),
      '>',
      xlate('> `**Italics is a term that refers to stylized characters, generally used to emphasize key points.`'),
      '',
      xlate('## Result'),
      '',
      xlate('Over 5,000 years of art from around the world is on display at the Metropolitan Museum of Art*. '),
      xlate('==The author does not intend to display this text in italics**;'),
      xlate('==the asterisks in the text are used to indicate the presence of footnotes.'),
      xlate('==However, Presentual Markdown interprets them as markers for the beginning and end of a span of emphasized text.'),
      '',
      xlate('*Located in New York City, New York, United States of America.'),
      '',
      xlate('**Italics is a term that refers to stylized characters, generally used to emphasize key points.'),
      '',
      xlate('To avoid issues like these, at times it is necessary to *escape* characters with special meaning in Presentual Markdown.'),
      xlate('==To \'escape\' a character means to declare that the character should be interpreted as nothing more than normal text.'),
      xlate('==This is done by inserting a backslash character (**\\\\**) immediately before the character being escaped.'),
      '',
      xlate('> Escaping every character with special meaning in Presentual Markdown is inefficient, and generally unnecessary.'),
      xlate('==This technique should only be used when a certain character is altering the intended presentation of the text.'),
      '',
      xlate('### Example'),
      '',
      xlate('> `Over 5,000 years of art from around the world is on display at the Metropolitan Museum of Art\\\\*.'),
      xlate('==The author does not intend to display this text in italics**;'),
      xlate('==the asterisks in the text are used to indicate the presence of footnotes.'),
      xlate('==However, the first asterisk in the paragraph was escaped, so the text will appear as intended.`'),
      '>',
      xlate('> `*Located in New York City, New York, United States of America.`'),
      '>',
      xlate('> `**Italics is a term that refers to stylized characters, generally used to emphasize key points.`'),
      '',
      xlate('## Result'),
      '',
      xlate('Over 5,000 years of art from around the world is on display at the Metropolitan Museum of Art\\*.'),
      xlate('==The author does not intend to display this text in italics**;'),
      xlate('==the asterisks in the text are used to indicate the presence of footnotes.'),
      xlate('==However, the first asterisk in the paragraph was escaped, so the text will appear as intended.'),
      '',
      xlate('*Located in New York City, New York, United States of America.'),
      '',
      xlate('**Italics is a term that refers to stylized characters, generally used to emphasize key points.'),
    ]
  },
  // paragraph
  {
    articleId: 'paragraph',
    title: xlate('Paragraph'),
    content: [
      xlate('# Paragraph'),
      '',
      xlate('A paragraph is the simplest element in Presentual Markdown.  To create one, just start typing.'),
      '',
      xlate('To create a new paragraph, leave one or more blank lines between the new paragraph and the previous.'),
      '',
      xlate('> **Note:** Presentual Markdown renders a group of blank lines as just one blank line'),
      '',
      xlate('#### Example:'),
      '',
      xlate('> `It was the best of times, it was the worst of times, it was the age of wisdom, it was the age of foolishness, it was the epoch of belief, it was the epoch of incredulity,'),
      xlate('it was the season of Light, it was the season of Darkness, it was the spring of hope, it was the winter of despair, we had everything before us, we had nothing before us,'),
      xlate('we were all going direct to Heaven, we were all going direct the other way—in short, the period was so far like the present period, that some of its noisiest authorities insisted on its being received,'),
      xlate('for good or for evil, in the superlative degree of comparison only.`'),
      '>',
      xlate('> `There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France. In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.`'),
      '',
      xlate('#### Result'),
      '',
      xlate('It was the best of times, it was the worst of times, it was the age of wisdom, it was the age of foolishness, it was the epoch of belief, it was the epoch of incredulity,'),
      xlate('==it was the season of Light, it was the season of Darkness, it was the spring of hope, it was the winter of despair, we had everything before us, we had nothing before us,'),
      xlate('==we were all going direct to Heaven, we were all going direct the other way—in short, the period was so far like the present period, that some of its noisiest authorities insisted on its being received,'),
      xlate('==for good or for evil, in the superlative degree of comparison only.'),
      '',
      xlate('There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.'),
      '',
      xlate('## Line Breaks'),
      '',
      xlate('Starting a new line just below the previous with no blank line in between will result in a line break in the text within the same paragraph.'),
      '',
      xlate('#### Example:'),
      '',
      xlate('> `There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('> In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.`'),
      '',
      xlate('#### Result'),
      '',
      xlate('There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.'),
    ]
  },
  // heading
  {
    articleId: 'heading',
    title: xlate('Heading'),
    content: [
      xlate('# Heading'),
      '',
      xlate('To create a heading within a section, start a new line with between one and six number signs/hashtags/pound signs (**#**), followed by a blank space, then the heading text.'),
      xlate('The number of number signs used corresponds to the *level* of the heading.'),
      '',
      xlate('Insert a blank line between the heading and the content beneath it.'),
      '',
      xlate('> Typically, heading levels are used to denote a subheading of a heading, e.g. heading level 2 (**##**) is used as a subheading of content directly under heading level 1 (**#**),'),
      xlate('heading level 3 (**###**) is used as a subheading of heading level 2, and so on.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `# Heading 1`'),
      '>',
      xlate('> `## Heading 2`'),
      '>',
      xlate('> `### Heading 3`'),
      '>',
      xlate('> `#### Heading 4`'),
      '>',
      xlate('> `##### Heading 5`'),
      '>',
      xlate('> `###### Heading 6`'),
      '',
      xlate('#### Result'),
      '',
      xlate('# Heading 1'),
      '',
      xlate('## Heading 2'),
      '',
      xlate('### Heading 3'),
      '',
      xlate('#### Heading 4'),
      '',
      xlate('##### Heading 5'),
      '',
      xlate('###### Heading 6'),
      '',
      xlate('## Alternate Syntax'),
      '',
      xlate('A level 1 heading can also be created by entering one or more equal sign characters (**=**) on the line directly below the heading text.'),
      '',
      xlate('A level 2 heading, in turn, can be created by entering one or more dash/hyphen characters (**-**) on the line directly below the heading text.'),
      '',
      xlate('> Heading level 2 is the lowest level possible using this syntax'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `Heading 1`'),
      xlate('> `=========`'),
      xlate('> '),
      xlate('> `Heading 2`'),
      xlate('> `---------`'),
      '',
      xlate('#### Result'),
      '',
      xlate('# Heading 1'),
      '',
      xlate('Heading 2'),
      xlate('---------'),
    ]
  },
  // bold
  {
    articleId: 'bold',
    title: xlate('Bold'),
    content: [
      xlate('# Bold Text'),
      '',
      xlate('To make a section of text appear in bold print, place two asterisk (*) or two underscore (_) characters at the beginning and end of the section.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The text inside the parenthesis (\\*\\*will be bold\\*\\*).  The rest will not be.`'),
      '>',
      xlate('> `Again, the text inside the parenthesis (\\_\\_will be bold\\_\\_).  The rest will not be.`'),
      '>',
      xlate('> `The same characters used to denote the \\*\\*beginning\\*\\* of a section of bold text must be used to denote its \\_\\_end\\_\\_. For this reason, the text inside the parenthesis (\\*\\*will not be bold\\_\\_).`'),
      '',
      xlate('#### Result'),
      '',
      xlate('> The text inside the parenthesis (**will be bold**).  The rest will not.'),
      '>',
      xlate('> Again, the text inside the parenthesis (__will be bold__).  The rest will not be.'),
      '>',
      xlate('> The same characters used to denote the **beginning** of a section of bold text must be used to denote its __end__. For this reason, the text inside the parenthesis (**will not be bold__).'),
    ]
  },
  // italic
  {
    articleId: 'italic',
    title: xlate('Italic'),
    content: [
      xlate('# Italicized Text'),
      '',
      xlate('To italicize a section of text, place a single asterisk (*) or underscore (_) character at the beginning and end of the section.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The text inside the parenthesis (\\*will be italicized\\*).  The rest will not be.`'),
      '>',
      xlate('> `Again, the text inside the parenthesis (\\_will be italicized\\_).  The rest will not be.`'),
      '>',
      xlate('> `The same characters used to denote the \\*beginning\\* of a section of italicized text must be used to denote its \\_end\\_. For this reason, the text inside the parenthesis (\\*will not be italicized\\_).`'),
      '',
      xlate('#### Result'),
      '',
      xlate('> The text inside the parenthesis (*will be italicized*).  The rest will not.'),
      '>',
      xlate('> Again, the text inside the parenthesis (_will be italicized_).  The rest will not be.'),
      '>',
      xlate('> The same characters used to denote the *beginning* of a section of italicized text must be used to denote its _end_. For this reason, the text inside the parenthesis (*will not be italicized_).'),
    ]
  },
  // link
  {
    articleId: 'link',
    title: xlate('Link'),
    content: [
      xlate('# Link (Hyperlink)'),
      '',
      xlate('Presentual Markdown supports linking to websites and other web resources from a discussion plan.'),
      xlate('These links will open in a browser tab or window separate from Presentual.'),
      '',
      xlate('> **Be careful!** Only link to pages and other material from trusted sites.'),
      '',
      xlate('There are two ways to create a link with Presentual Markdown, described below.'),
      xlate('When creating a link using either method, the *protocol* (typically `http://`, or `https://`) portion of the URL must be included.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `This link **will not** work as intended:  <www.si.edu/>`'),
      xlate('> '),
      xlate('> `This link **will** work:  <https://www.si.edu/>`'),
      '',
      xlate('#### Result'),
      '',
      xlate('This **will not** work:  <www.si.edu/>'),
      '',
      xlate('This **will** work:  <https://www.si.edu/>'),
      '',
      xlate('## Simple Link'),
      '',
      xlate('Create a link by enclosing a URL with opening and closing angle brackets (**< >**).'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The Smithsonian Institution (<https://www.si.edu>) is the world\'s largest museum, education, and research complex with 21 museums and the National Zoo.`'),
      '',
      xlate('##### Result'),
      '',
      xlate('The Smithsonian Institution (<https://www.si.edu>) is the world\'s largest museum, education, and research complex with 21 museums and the National Zoo.'),
      '',
      xlate('## Link Text'),
      '',
      xlate('To create text that serves as a web link, enclose the text in square brackets (**[ ]**), followed by the URL that the text will link to, enclosed in parentheses.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The [Smithsonian Institution](https://www.si.edu) is the world\'s largest museum, education, and research complex with 21 museums and the National Zoo.`'),
      '',
      xlate('#### Result'),
      '',
      xlate('The [Smithsonian Institution](https://www.si.edu) is the world\'s largest museum, education, and research complex with 21 museums and the National Zoo.'),
    ]
  },
  // image
  {
    articleId: 'image',
    title: xlate('Image'),
    content: [
      xlate('# Image'),
      '',
      xlate('Images from the web may be included in a discussion plan with Presentual Markdown.'),
      '',
      xlate('> **Be careful!** Only reference images from trusted sites.'),
      xlate('Remember that material used in a discussion plan must comply with Presentual\'s [Terms of Service]({site}/terms-of-service).', { site: window.location.origin }),
      '',
      xlate('There are two ways to include an image in a discussion plan, described below.'),
      xlate('When including an image using either method, the *protocol* (typically `http://`, or `https://`) portion of the image\'s URL must be included.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `This image reference **will not** work as intended:  !<{site}/presentual-logo.jpg>`', { site: window.location.host }),
      xlate('> '),
      xlate('> `This link **will** work:  !<{site}/presentual-logo.jpg>`', { site: window.location.origin }),
      '',
      xlate('#### Result'),
      '',
      xlate('This image reference **will not** work as intended:  !<{site}/presentual-logo.jpg>', { site: window.location.host }),
      '',
      xlate('This link **will** work:  !<{site}/presentual-logo.jpg>', { site: window.location.origin }),
      '',
      xlate('## Basic Image Reference'),
      '',
      xlate('Start the reference with an exclamation point (**!**), then enclose image\'s URL with opening and closing angle brackets (**< >**).'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `!<{site}/presentual-logo.jpg>`', { site: window.location.origin }),
      '',
      xlate('##### Result'),
      '',
      xlate('!<{site}/presentual-logo.jpg>', { site: window.location.origin }),
      '',
      xlate('## Image with Alternative (Alt) Text'),
      '',
      xlate('Start the reference with an exclamation point (**!**), then enclose the alt text in square brackets (**[ ]**), followed by the image\'s URL, enclosed in parentheses.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `![Presentual Logo]({site}/presentual-logo.jpg)`', { site: window.location.origin }),
      '',
      xlate('#### Result'),
      '',
      xlate('![Presentual Logo]({site}/presentual-logo.jpg)', { site: window.location.origin }),
    ]
  },
  // list
  {
    articleId: 'list',
    title: xlate('List'),
    content: [
      xlate('# List'),
      '',
      xlate('Presentual Markdown supports creation of lists that are ordered, unordered, or a mix of the two.'),
      '',
      xlate('## Ordered List'),
      '',
      xlate('To create an ordered list, begin the line for each item with any number, immediately followed by a period and a blank space.'),
      '',
      xlate('> The value of the number of the used is not relevant.'),
      xlate('If different numbers are used, their order is likewise not relevant.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `1. First item`'),
      xlate('> `1. Second item`'),
      xlate('> `1. Third item`'),
      '',
      xlate('#### Result'),
      '',
      xlate('1. First item'),
      xlate('1. Second item'),
      xlate('1. Third item'),
      '',
      xlate('## Unordered List'),
      '',
      xlate('To create an unordered (bulleted) list, begin the line for each item with either a dash (**-**), plus sign (**+**), or asterisk (**\\***)immediately followed by a blank space.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `- First point`'),
      xlate('> `- Second point`'),
      xlate('> `- Third point`'),
      '',
      xlate('#### Result'),
      '',
      xlate('- First point'),
      xlate('- Second point'),
      xlate('- Third point'),
      '',
      xlate('## Sublist'),
      '',
      xlate('Both ordered and unordered list items may contain a sublist, which may in turn be either ordered or unordered.'),
      xlate('To create a sublist, indent each item with four spaces more than its parent list item.'),
      xlate('In this way, Presentual Markdown supports multiple levels of list nesting.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `1. First main item`'),
      xlate('> &nbsp;&nbsp;&nbsp;&nbsp;`1. First sublist item`'),
      xlate('> &nbsp;&nbsp;&nbsp;&nbsp;`1. Second sublist item`'),
      xlate('> &nbsp;&nbsp;&nbsp;&nbsp;`1. Third sublist item`'),
      xlate('> `1. Second main item`'),
      xlate('> &nbsp;&nbsp;&nbsp;&nbsp;`- Sublist point A`'),
      xlate('> &nbsp;&nbsp;&nbsp;&nbsp;`- Sublist point B`'),
      xlate('> &nbsp;&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp;`- Subpoint of sublist point B`'),
      xlate('> &nbsp;&nbsp;&nbsp;&nbsp;`- Sublist point C`'),
      xlate('> `1. Third main item`'),
      '',
      xlate('#### Result'),
      '',
      xlate('1. First main item'),
      xlate('    1. First sublist item'),
      xlate('    1. Second sublist item'),
      xlate('    1. Third sublist item'),
      xlate('1. Second main item'),
      xlate('    - Sublist point A'),
      xlate('    - Sublist point B'),
      xlate('        - Subpoint of sublist point B'),
      xlate('    - Sublist point C'),
      xlate('1. Third main item'),
    ]
  },
  // underline
  {
    articleId: 'underline',
    title: xlate('Underline'),
    content: [
      xlate('# Underlined Text'),
      '',
      xlate('To underline a section of text, place **three** equal sign characters (**=**) at the beginning and end of the section.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The text inside the parenthesis (===will appear underlined===).`'),
      '',
      xlate('#### Result'),
      '',
      xlate('The text inside the parenthesis (===will appear underlined===).'),
    ]
  },
  // highlight
  {
    articleId: 'highlight',
    title: xlate('Highlight'),
    content: [
      xlate('# Highlighted Text'),
      '',
      xlate('To highlight a span of text, place **two** equal sign characters (**=**) at the beginning and end of the text being highlighted.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The text inside the parenthesis (==will appear highlighted==).`'),
      '',
      xlate('#### Result'),
      '',
      xlate('The text inside the parenthesis (==will appear highlighted==).'),

    ]
  },
  // strikethrough
  {
    articleId: 'strikethrough',
    title: xlate('Strikethrough'),
    content: [
      xlate('# Strikethrough Text'),
      '',
      xlate('To strike through a span of text, place **two** tilde characters (**~**) at the beginning and end of the text.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The text inside the parenthesis (~~will have a line going through it~~).`'),
      '',
      xlate('#### Result'),
      '',
      xlate('The text inside the parenthesis (~~will have a line going through it~~).'),
    ]
  },
  // superscript
  {
    articleId: 'superscript',
    title: xlate('Superscript'),
    content: [
      xlate('# Superscript'),
      '',
      xlate('Create superscript text by enclosing its content with caret (**^**) characters.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `2^4^ equals 16.`'),
      '',
      xlate('#### Result'),
      '',
      xlate('2^4^ equals 16.'),
    ]
  },
  // subscript
  {
    articleId: 'subscript',
    title: xlate('Subscript'),
    content: [
      xlate('# Subscript'),
      '',
      xlate('Create subscript text by enclosing its content with tilde(**~**) characters.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `The chemical formula for water is H~2~O.`'),
      '',
      xlate('#### Result'),
      '',
      xlate('The chemical formula for water is H~2~O.'),
    ]
  },
  // dash
  {
    articleId: 'dash',
    title: xlate('Dash'),
    content: [
      xlate('# Dash'),
      '',
      xlate('Presentual Markdown supports three types of dashes.  All varieties are created with the dash character (**-**).'),
      '',
      xlate(' ## Supported Varieties'),
      '',
      xlate('- **Hyphen** -- one dash'),
      xlate('- **En Dash** -- two dashes'),
      xlate('- **Em Dash** -- three dashes'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `A hyphen is used in compound words, such as cul-de-sac`'),
      '>',
      xlate('> `An en dash is appropriate to use when referring to a range of values, e.g., ages 30--40.`'),
      '>',
      xlate('> `Use an em dash to signal a break in thought, or to emphasize the phrase to follow:`'),
      '>',
      xlate('> `> *Billy saved $30---about 50--65% of his allowance for that month---to buy the t-shirt.*`'),
      '',
      xlate('#### Result'),
      '',
      xlate('A hyphen is used in compound words, such as cul-de-sac'),
      '',
      xlate('An en dash is appropriate to use when referring to a range of values, e.g., ages 30--40.'),
      '',
      xlate('Use an em dash to signal a break in thought, or to emphasize the phrase to follow:'),
      '',
      xlate('> *Billy saved $40---about 50--65% of his allowance for that month---to buy the t-shirt.*'),
    ]
  },
  // table
  {
    articleId: 'table',
    title: xlate('Table'),
    content: [
      xlate('# Table'),
      '',
      xlate('Add a table to a discussion plan by creating one or more *table rows*.'),
      xlate('To create a table row, start a new line with a pipe character (**|**).'),
      xlate('Indicate the end of the content of each column with a pipe character.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `| Apples | $0.85 | 2 | $1.70 |`'),
      '',
      xlate('#### Result'),
      '',
      xlate('| Apples | $0.85 | 2 | $1.70 |'),
      '',
      xlate('## Adding Rows'),
      '',
      xlate('Add a row to a table by defining the row directly beneath the previous one, with no empty lines between them.'),
      '',
      xlate('> **Note:** Two table rows with an empty line in between will be displayed as two separate tables.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `| Apples | $0.85 | 2 | $1.70 |`'),
      xlate('> `| Oranges | $1.00 | 3 | $3.00 |`'),
      xlate('> `| Mangoes | $1.30 | 1 | $1.30 |`'),
      '',
      xlate('#### Result'),
      '',
      xlate('| Apples | $0.85 | 2 | $1.70 |'),
      xlate('| Oranges | $1.00 | 3 | $3.00 |'),
      xlate('| Mangoes | $1.30 | 1 | $1.30 |'),
      '',
      xlate('## Arranging Columns'),
      '',
      xlate('Unlike standard Markdown, Presentual Markdown does not require that each row be defined with the same number of columns.'),
      xlate('Instead, all rows are rendered with the same number of columns as the row with the most columns defined.'),
      xlate('Empty columns are appended to rows with fewer columns as needed for uniformity.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `| Apples | McIntosh | $0.85 | 2 | $1.70 |`'),
      xlate('> `| Oranges | $1.00 | 3 | $3.00 |`'),
      xlate('> `| Mangoes | $1.30 | 1 | $1.30 |`'),
      '',
      xlate('#### Result'),
      '',
      xlate('| Apples | McIntosh | $0.85 | 2 | $1.70 |'),
      xlate('| Oranges | $1.00 | 3 | $3.00 |'),
      xlate('| Mangoes | $1.30 | 1 | $1.30 |'),
      '',
      xlate('### Merging Columns'),
      '',
      xlate('To merge two or more row columns together, add additional pipe characters (with no spaces in between) at the end of a column according to the number of columns it will span.'),
      '',
      xlate('> **Note:** Adding one or more blank spaces between two pipes will result in the creation of an empty column'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `| Apples | McIntosh | $0.85 | 2 | $1.70 |`'),
      xlate('> `| Oranges || $1.00 | 3 | $3.00 |`'),
      xlate('> `| Mangoes || $1.30 | 1 | $1.30 |`'),
      '',
      xlate('#### Result'),
      '',
      xlate('| Apples | McIntosh | $0.85 | 2 | $1.70 |'),
      xlate('| Oranges || $1.00 | 3 | $3.00 |'),
      xlate('| Mangoes || $1.30 | 1 | $1.30 |'),
      '',
      xlate('## Table Header'),
      '',
      xlate('To indicate that the first row of a table is its header, add a row directly under the header row with each column consisting of dash (**-**) characters.'),
      xlate('This row acts as a separator between the table header and the table body.'),
      '',
      xlate('> **Note:** All rows defined before the header/body separator row are rendered as part of the table header.'),
      xlate('Therefore, a table may consist of multiple header rows.  If header row(s) are defined, there must be a least one row in the table body.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `| Item / Type || Unit Price | Qty | Line Total |`'),
      xlate('> `| ------ | -------- | ----- | - | ----- |`'),
      xlate('> `| Apples | McIntosh | $0.85 | 2 | $1.70 |`'),
      xlate('> `| Oranges || $1.00 | 3 | $3.00 |`'),
      xlate('> `| Mangoes || $1.30 | 1 | $1.30 |`'),
      '',
      xlate('#### Result'),
      '',
      xlate('| Item / Type || Unit Price | Qty | Line Total |'),
      xlate('| ------ | -------- | ----- | - | ----- |'),
      xlate('| Apples | McIntosh | $0.85 | 2 | $1.70 |'),
      xlate('| Oranges || $1.00 | 3 | $3.00 |'),
      xlate('| Mangoes || $1.30 | 1 | $1.30 |'),
      '',
      xlate('## Text Alignment'),
      '',
      xlate('Align text for each columns to the left, right, or center by adding a colon (**:**) to the left, right, or on both sides of the dash characters within the header/body separator row.'),
      '',
      xlate('> **Note:**  A table header is not required to specify column alignments.'),
      xlate('The header/body separator row may serve as the first row in the table in order to specify text alignment for the columns below.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `| Item / Type || Unit Price | Qty | Line Total |`'),
      xlate('> `| :------ | :-------- | :-----: | :-: | -----: |`'),
      xlate('> `| Apples | McIntosh | $0.85 | 2 | $1.70 |`'),
      xlate('> `| Oranges || $1.00 | 3 | $3.00 |`'),
      xlate('> `| Mangoes || $1.30 | 1 | $1.30 |`'),
      '',
      xlate('#### Result'),
      '',
      xlate('| Item / Type || Unit Price | Qty | Line Total |'),
      xlate('| :------ | :-------- | :-----: | :-: | -----: |'),
      xlate('| Apples | McIntosh | $0.85 | 2 | $1.70 |'),
      xlate('| Oranges || $1.00 | 3 | $3.00 |'),
      xlate('| Mangoes || $1.30 | 1 | $1.30 |'),
      '',
      xlate('## Table Footer'),
      '',
      xlate('To indicate that the last row of a table is its footer, add a row directly above the footer row with one or more columns consisting of equal sign (**=**) characters.'),
      xlate('This row acts as a separator between the table body and the table footer.'),
      '',
      xlate('> **Note:** Similar to the header/body separator row, all rows defined after the footer separator row are rendered as part of the table footer.'),
      xlate('Therefore, a table may have multiple footer rows.'),
      xlate('In order define footer row(s), there must be a least one row in the table body.'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `| Item / Type || Unit Price | Qty | Line Total |`'),
      xlate('> `| :------ | :-------- | :-----: | :-: | -----: |`'),
      xlate('> `| Apples | McIntosh | $0.85 | 2 | $1.70 |`'),
      xlate('> `| Oranges || $1.00 | 3 | $3.00 |`'),
      xlate('> `| Mangoes || $1.30 | 1 | $1.30 |`'),
      xlate('> `| ============================ |`'),
      xlate('> `| Grand Total: |||| $6.00 |`'),
      '',
      xlate('#### Result'),
      '',
      xlate('| Item / Type || Unit Price | Qty | Line Total |'),
      xlate('| :------ | :-------- | :-----: | :-: | -----: |'),
      xlate('| Apples | McIntosh | $0.85 | 2 | $1.70 |'),
      xlate('| Oranges || $1.00 | 3 | $3.00 |'),
      xlate('| Mangoes || $1.30 | 1 | $1.30 |'),
      xlate('| ============================ |'),
      xlate('| Grand Total: |||| $6.00 |'),
    ]
  },
  // blockquote
  {
    articleId: 'bq',
    title: xlate('Blockquote'),
    content: [
      xlate('# Blockquote'),
      '',
      xlate('To create a blockquote, start a line with a right angle bracket, also known as a greater than sign (**>**) and a blank space, then start typing the blockquote\'s text.'),
      '',
      xlate('#### Example:'),
      '',
      xlate('> `> There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.`'),
      '',
      xlate('#### Result'),
      '',
      xlate('> There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.'),
      '',
      xlate('## Multiple Paragraphs'),
      '',
      xlate('To create multiple paragraphs within a blockquote, insert a line with only a right angle bracket between each paragraph as a delineator.'),
      '',
      xlate('> **Note:** Presentual Markdown will render multiple consecutive paragraph delineators within a blockquote as just one'),
      '',
      xlate('#### Example:'),
      '',
      xlate('> `> There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.`'),
      xlate('> `>`'),
      xlate('> `> ***Charles Dickens***`'),
      '',
      xlate('#### Result'),
      '',
      xlate('> There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.'),
      xlate('> '),
      xlate('> ***Charles Dickens***'),
      '',
      '',
      xlate('## Line Breaks'),
      '',
      xlate('To add a line break in a paragraph, begin a new line with a right angle bracket just below the previous line in the blockquote.'),
      '',
      xlate('#### Example:'),
      '',
      xlate('> `> There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.`'),
      xlate('> `>`'),
      xlate('> `> ***Charles Dickens***`'),
      xlate('> `> *A Tale of Two Cities*`'),
      '',
      xlate('#### Result'),
      '',
      xlate('> There were a king with a large jaw and a queen with a plain face, on the throne of England; there were a king with a large jaw and a queen with a fair face, on the throne of France.'),
      xlate('In both countries it was clearer than crystal to the lords of the State preserves of loaves and fishes, that things in general were settled for ever.'),
      xlate('> '),
      xlate('> ***Charles Dickens***'),
      xlate('> *A Tale of Two Cities*'),
    ]
  },
  // horizontal rule
  {
    articleId: 'rule',
    title: xlate('Rule'),
    content: [
      xlate('# Horizontal Rule'),
      '',
      xlate('To create a horizontal line that separates content within a section,'),
      xlate('==enter a line consisting of either one or more equal sign characters (**=**),'),
      xlate('==or one or more dash (hyphen) characters (**-**). '),
      '',
      xlate('> **Note:** There must be a blank line directly above and below the horizontal rule'),
      '',
      xlate('#### Example'),
      '',
      xlate('> `Use equal sign characters to render a thick rule:`'),
      '>',
      xlate('> `===`'),
      '>',
      xlate('> `Use dash characters to render a thin rule:`'),
      '>',
      xlate('> `---`'),
      '>',
      xlate('> `Notice that there are blank lines above and below the rule lines.`'),
      '',
      xlate('#### Result'),
      '',
      xlate('Use equal sign characters to render a thick rule:'),
      '',
      xlate('==='),
      '',
      xlate('Use dash characters to render a thin rule:'),
      '',
      xlate('---'),
      '',
      xlate('Notice that there are blank lines above and below the rule lines.'),
    ]
  },
  // code
  {
    articleId: 'code',
    title: xlate('Code'),
    content: [
      xlate('# Code Text'),
      '',
      xlate('A segment of `code` text is rendered with a font typically associated with the code written by a computer programmer. '),
      xlate('==To make a text segment appear as code, start and end the segment with an accent grave (**`**) character.'),
      '',
      xlate('#### Example'),
      xlate('> `**The following is JavaScript:** \\`const greeting = \'Hello!\';\\``'),
      '',
      xlate('#### Result'),
      '',
      xlate('**The following is JavaScript:** `const greeting = \'Hello!\';`'),
      '',
      xlate('## Markdown Notation'),
      '',
      xlate('Presentual Markdown notation normally used to decorate text has no effect on text within a code segment, and is rendered exactly as it was typed.'),
      '',
      xlate('#### Example'),
      xlate('> `**The following is JavaScript:** \\`const greeting = \'**Hello!**\';\\``'),
      '',
      xlate('#### Result'),
      '',
      xlate('**The following is JavaScript:** `const greeting = \'**Hello!**\';`'),
    ]
  },
];